
import { apiClient } from '@/lib/api-client';
import type { SleepEvent, Child, AnalyzeSleepPatternsInput, GenerateSleepGoalsInput } from '@/lib/types';
import { format, parseISO, subDays } from 'date-fns';

// Define a type for events passed to server actions, where time is a string
type ActionSleepEvent = Omit<SleepEvent, 'time'> & { time: string };

function formatSleepDataForAI(events: ActionSleepEvent[]): string {
  if (events.length === 0) {
    return 'No sleep data available for the last 7 days.';
  }

  // Group events by day
  const eventsByDay: { [key: string]: ActionSleepEvent[] } = {};
  events.forEach(event => {
    const dayKey = format(parseISO(event.time), 'yyyy-MM-dd');
    if (!eventsByDay[dayKey]) {
      eventsByDay[dayKey] = [];
    }
    eventsByDay[dayKey].push(event);
  });

  // Sort days chronologically
  const sortedDays = Object.keys(eventsByDay).sort();

  return sortedDays.map(dayKey => {
    const dayEvents = eventsByDay[dayKey];
    const date = parseISO(dayEvents[0].time);

    let daySummary = `On ${format(date, 'PPP')}:`;

    const eventStrings = dayEvents.map(e => {
      let eventStr = `${e.type} at ${format(parseISO(e.time), 'HH:mm')}`;
      if (e.type === 'night_wake' && e.duration) {
        eventStr += ` for ${e.duration} minutes`;
      }
      if (e.notes) {
        eventStr += ` (Notes: ${e.notes})`;
      }
      return eventStr;
    });

    daySummary += ` Events logged: ${eventStrings.join(', ')}.`;

    return daySummary;
  }).join('\n');
}

export function calculateAgeInMonths(birthMonth: number, birthYear: number): number {
  const today = new Date();
  const birthDate = new Date(birthYear, birthMonth - 1);
  let ageYears = today.getFullYear() - birthDate.getFullYear();
  let ageMonths = today.getMonth() - birthDate.getMonth();
  if (ageMonths < 0 || (ageMonths === 0 && today.getDate() < birthDate.getDate())) {
    ageYears--;
    ageMonths += 12;
  }
  return Math.max(0, ageYears * 12 + ageMonths);
}

export function formatChildAge(birthMonth: number, birthYear: number): string {
  const months = calculateAgeInMonths(birthMonth, birthYear);
  if (months < 1) return 'newborn (under 1 month old)';
  if (months < 24) return `${months} months old`;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (remainingMonths === 0) return `${years} years old`;
  return `${years} years, ${remainingMonths} months old`;
}

export async function getSleepAnalysis(events: ActionSleepEvent[], history: AnalyzeSleepPatternsInput['history'], child: Child) {
  // Use last 7 days of data for analysis
  const sevenDaysAgo = subDays(new Date(), 7);
  const recentEvents = events.filter(event => parseISO(event.time) >= sevenDaysAgo);
  const sleepData = formatSleepDataForAI(recentEvents);

  const ageText = formatChildAge(child.birthMonth, child.birthYear);
  const displayGender = child.gender === 'other' ? 'child' : child.gender;

  const now = new Date();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const input: AnalyzeSleepPatternsInput = {
    sleepData,
    history,
    childInfo: `The child is ${child.name}, ${ageText}, ${displayGender}.`,
    currentTime: now.toISOString(),
    timezone,
  };

  try {
    const result = await apiClient.analyzeSleepPatterns(input);
    return result;
  } catch (error) {
    console.error('Error analyzing sleep patterns:', error);
    if (error instanceof Error) {
      return { success: false, error: error.message };
    }
    return { success: false, error: 'Failed to get sleep analysis.' };
  }
}

export async function getAIGoals(child: Child, events: ActionSleepEvent[], preferences: string) {
  // Use last 7 days for context
  const sevenDaysAgo = subDays(new Date(), 7);
  const recentEvents = events.filter(event => parseISO(event.time) >= sevenDaysAgo);
  const currentSleepCycle = formatSleepDataForAI(recentEvents);
  const ageMonths = calculateAgeInMonths(child.birthMonth, child.birthYear);
  const ageInYears = Math.round((ageMonths / 12) * 10) / 10;
  const displayGender = child.gender === 'other' ? 'child' : child.gender;

  const input: GenerateSleepGoalsInput = {
    childId: child.id,
    childAge: ageInYears,
    childAgeMonths: ageMonths,
    childGender: displayGender,
    currentSleepCycle,
    userPreferences: preferences || 'No specific preferences provided.',
  };

  try {
    const result = await apiClient.generateSleepGoals(input);
    return result;
  } catch (error) {
    console.error('Error generating AI goals:', error);
    if (error instanceof Error) {
      return { success: false, error: error.message };
    }
    return { success: false, error: 'Failed to generate AI sleep goals.' };
  }
}
